import React, { useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import cn from 'clsx'

import { SearchMenuStates } from '../type'
import DateInputDesktop from './SearchDateInput/SearchDateInputDesktop'
import TravellersInputDesktop from './SearchTravellersInput/SearchTravellersInputDesktop'

import { isRoomsInvalid } from 'lib/searchUtils'

import TextButton from 'components/Luxkit/Button/TextButton'

import zIndex from 'styles/tools/z-index'
import Backdrop from 'components/utils/Backdrop'
import { DATE_SEARCH_OPTION_IDS, LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL, LOCATION_SEARCH_INPUT_TITLE_LABEL } from 'constants/search'
import config from 'constants/config'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import SearchLocationInput from './SearchLocationInput/SearchLocationInput'

const InputWrapper = styled.section`
  ${mediaQueryUp.sm} {
    width: 100%;
    position: relative;
    gap: ${rem(8)};
    display: grid;
    grid-template-areas:
      "location-input location-input location-input"
      "date-input traveller-input actions";
    grid-template-columns: minmax(${rem(348)}, 1fr) minmax(${rem(180)}, 1fr) ${rem(112)};

    &.has-dropdown {
      z-index: ${zIndex.menu};
    }
  }

  ${mediaQueryUp.md} {
    grid-template-areas: "location-input date-input traveller-input actions";
    grid-template-columns: 1fr ${rem(320)} ${rem(154)} ${rem(112)};
  }
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;

  &.location-input {
    grid-area: location-input;
  }

  &.date-input {
    grid-area: date-input;
  }

  &.traveller-input {
    grid-area: traveller-input;
  }

  &.actions {
    grid-area: actions;
  }
`

const StyledBackdrop = styled(Backdrop)`
  z-index: 0;
  &.visible {
    background-color: rgba(0, 0, 0, 0) !important; /* to prevent original background grey color when the backdrop open too fast too often */
    transition: none;
  }
`

const SearchButton = styled(TextButton)`
  height: ${rem(50)};
`

interface Props {
  searchItem: App.SearchItem;
  rooms: App.Occupants[];
  openDatePickerMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
  openTravellerMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
  openLocationMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
  closeMenu: () => void;
  activeMenu: SearchMenuStates;
  onRoomChange: (rooms: Array<App.Occupants>) => void;
  onRoomsApply: () => void;
  onDatesApply: () => void;
  onDatesDecline: () => void;
  showRoomErrors: boolean;
  dropdownToggled: boolean;
  backdropOnClick?: () => void;
  backdropVisibility?: boolean;
  anytimeDateSelected?: boolean;
  selectAnytimeDate?: (shouldApply: boolean) => void;
  selectSpecificDates?: () => void;
  selectFlexibleDates?: () => void;
  onFlexibleMonthSelected?: () => void;
  onFlexibleNightsSelected?: () => void;
  dateSearchOptionId?: DATE_SEARCH_OPTION_IDS
  onLocationChange?: (item: App.SearchItem) => void;
  saleUnit?: string;
  typeaheadTypes?: Array<App.SearchPlaceType>;
  recentSearches?: Array<App.FullRecentSearch>;
}

const SearchDesktopInputs = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    activeMenu,
    searchItem,
    rooms,
    openTravellerMenu,
    openLocationMenu,
    closeMenu,
    openDatePickerMenu,
    onRoomChange,
    onRoomsApply,
    onDatesApply,
    onDatesDecline,
    showRoomErrors,
    dropdownToggled,
    backdropOnClick,
    backdropVisibility,
    anytimeDateSelected,
    selectAnytimeDate,
    selectSpecificDates,
    selectFlexibleDates,
    dateSearchOptionId,
    onLocationChange,
    saleUnit,
    typeaheadTypes,
    recentSearches,
  } = props

  const { checkinDate, checkoutDate } = useContext(GlobalSearchStateContext)

  const isFormInvalid = useMemo(() => isRoomsInvalid(rooms), [rooms])

  const datesSelected = !!(checkinDate && checkoutDate)
  const isSearchDisabled = config.businessTraveller.currentAccountMode === 'business' && !datesSelected

  return <>
    <InputWrapper className={cn({ 'has-dropdown': dropdownToggled })}>
      <InputContainer className="location-input">
        <SearchLocationInput
          label={LOCATION_SEARCH_INPUT_TITLE_LABEL}
          placeholder={LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL}
          placeTypes={typeaheadTypes}
          onChange={onLocationChange}
          variant="dropdown"
          inputRef={ref}
          recentSearchItems={recentSearches}
          onLocationToggle={openLocationMenu}
        />
      </InputContainer>
      <InputContainer className="date-input">
        <DateInputDesktop
          onDateToggle={openDatePickerMenu}
          isToggled={activeMenu === SearchMenuStates.Dates}
          onDatesApply={onDatesApply}
          onDatesDecline={onDatesDecline}
          isLastStep={searchItem && rooms.length > 0}
          anytimeDateSelected={anytimeDateSelected}
          selectAnytimeDate={selectAnytimeDate}
          selectSpecificDates={selectSpecificDates}
          selectFlexibleDates={selectFlexibleDates}
          dateSearchOptionId={dateSearchOptionId}
        />
      </InputContainer>
      <InputContainer className="traveller-input">
        <TravellersInputDesktop
          rooms={rooms}
          onTravellersToggle={openTravellerMenu}
          isToggled={activeMenu === SearchMenuStates.Rooms}
          onRoomChange={onRoomChange}
          onRoomsApply={onRoomsApply}
          closeMenu={closeMenu}
          isLastStep={!!searchItem}
          error={showRoomErrors && isFormInvalid}
          saleUnit={saleUnit}
        />
      </InputContainer>
      <InputContainer className="actions">
        <SearchButton disabled={isSearchDisabled} kind="primary" type="submit" size="large">Search</SearchButton>
      </InputContainer>
    </InputWrapper>
    <StyledBackdrop
      visible={backdropVisibility || activeMenu !== SearchMenuStates.Closed}
      onClick={backdropOnClick || closeMenu}
    />
  </>
})

export default SearchDesktopInputs
